import { NgZone } from '@angular/core';
import {
  createSentryErrorProbe,
  SentryErrorProbe,
} from './sentry-error-probes';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  Accordion,
  AccordionPanel,
  AccordionHeader,
  AccordionContent,
} from 'primeng/accordion';
import { logCorrelationId } from './structured-logger.service';
import { LogTestScenario, StructuredEvent } from './structured-log-policy';
import { LogTestResult, LoggingTestService } from './logging-test.service';
import { runSentryTraceProbe } from './sentry-trace-probe';
import { runSentryReplayProbe } from './sentry-replay-probe';

type ScenarioGroup =
  | 'fa'
  | 'auth'
  | 'connectors'
  | 'navi'
  | 'flags'
  | 'api'
  | 'actions'
  | 'outcomes'
  | 'options'
  | 'goals'
  | 'features';

interface LoggingScenario {
  id: LogTestScenario;
  group: ScenarioGroup;
  label: string;
  detail: string;
  outcome: string;
  realCause: string;
  meaning: string;
  coverageNote?: string;
  events: Array<{ name: StructuredEvent; level: 'Warning' | 'Error' | 'Info' }>;
}

@Component({
  selector: 'log-test-panel',
  imports: [Accordion, AccordionPanel, AccordionHeader, AccordionContent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './logging-test-panel.component.html',
  styleUrl: './logging-test-panel.component.scss',
})
export class LoggingTestPanelComponent {
  private readonly testing = inject(LoggingTestService);
  private readonly zone = inject(NgZone);
  readonly errorProbeStatus = signal<string | null>(null);
  readonly traceProbeStatus = signal<string | null>(null);
  readonly traceProbeBusy = signal(false);
  readonly replayProbeBusy = signal(false);
  readonly replayProbeVisible = signal(false);
  readonly replayProbeStatus = signal<string | null>(null);
  readonly enabled = this.testing.enabled();
  readonly testRunId = signal(logCorrelationId());
  readonly busy = signal(false);
  readonly activeScenario = signal<LogTestScenario | null>(null);
  readonly results = signal<Array<LogTestResult & { label: string }>>([]);
  readonly deliveryMessages: Record<LogTestResult['flush'], string> = {
    completed:
      'Sentry SDK flush completed. Confirm receipt in Sentry using the run ID above.',
    timed_out: 'The Sentry SDK flush timed out. Delivery is unconfirmed.',
    failed:
      'The Sentry SDK could not finish flushing. Delivery is unconfirmed.',
    disabled: 'Logging is disabled. No test events were generated.',
  };
  readonly scenarios: LoggingScenario[] = [
    {
      id: 'action_assignment_unverified',
      group: 'actions',
      label: 'Saved Action assignee could not be verified',
      outcome: 'Saved; assignment uncertain',
      detail: 'Simulate a failed verification after a successful save.',
      realCause:
        'The Action save returns an unexpected assignee and the follow-up read fails or cannot verify the same Action and client. The modal retains the saved result and prevents repeating creation.',
      meaning:
        'One write was confirmed, while assignment verification remains uncertain. A confirmed mismatch is blocked with business_rule; a later successful edit retry is recovery. The modal owns this log so the store does not duplicate it.',
      events: [{ name: 'fa.action.outcome_unknown', level: 'Warning' }],
    },
    {
      id: 'api_failed',
      group: 'api',
      label: 'API request failed',
      outcome: 'Request failure',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'A request rejects because the network is unavailable, it times out, or its service returns an error status. The lower-level wrapper owns this diagnostic, including direct callers.',
      meaning:
        'One request diagnostic carries a fixed route template, method, status, duration and generated request ID. Related FA logs may carry that request ID; count FA outcomes separately.',
      events: [
        {
          name: 'api.request.failed',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'contract_mismatch',
      group: 'api',
      label: 'Unexpected response shape',
      outcome: 'Contract mismatch',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'Accounts inventory, account detail, transactions, or Financial Snapshot returns a payload that fails its typed response guard. Expected empty inventory and empty snapshots remain normal states.',
      meaning:
        'The contract name identifies the failing guard. No response keys, bodies or financial data are included.',
      events: [
        {
          name: 'api.response.contract_mismatch',
          level: 'Error',
        },
      ],
    },
    {
      id: 'action_recovered',
      group: 'actions',
      label: 'Action conflict and retry',
      outcome: 'Recovered save',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'An Action create, edit, status change or reassignment is rejected by the service, then an explicit retry succeeds. Care Plan rows, the shared editor and the standalone Home editor are covered.',
      meaning:
        'A new attempt ID shares the original interaction ID. This example uses an edit conflict. Ordinary validation, cancelled dialogs and disabled controls are excluded.',
      events: [
        {
          name: 'fa.action.blocked',
          level: 'Warning',
        },
        {
          name: 'fa.action.recovered',
          level: 'Info',
        },
      ],
    },
    {
      id: 'outcome_partial',
      group: 'outcomes',
      label: 'Outcome move interrupted',
      outcome: 'Partially saved; final result unknown',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'Moving an Outcome to another case can update its content, unlink the source and link destination Options. A later request may fail after an earlier step committed.',
      meaning:
        'Completed and planned operation counts expose partial progress. A failed write response remains uncertain; existing reconciliation reads determine what can safely be retried. No ATI values or Outcome wording are logged.',
      events: [
        {
          name: 'fa.action.outcome_unknown',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'option_qualification_failed',
      group: 'options',
      label: 'Saved Option could not reach Navi',
      outcome: 'Qualification handoff failed',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'The Option saves successfully, but the app cannot submit its qualification request to Navi. The saved Option is retained for retry.',
      meaning:
        'This is care_plan.option.qualify at the qualification stage, separate from saving the Option. Navi acceptance is not proof that qualification completed.',
      events: [
        {
          name: 'fa.action.failed',
          level: 'Error',
        },
      ],
    },
    {
      id: 'option_override_blocked',
      group: 'options',
      label: 'Qualification override rejected',
      outcome: 'Blocked override',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'An authorized qualification override submission is rejected by the API because permissions or current state changed. Creating, editing and ordinary status changes use their own action names.',
      meaning:
        'The diagnostic identifies the override operation without including Option details or the qualification prompt.',
      events: [
        {
          name: 'fa.action.blocked',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'goal_partial',
      group: 'goals',
      label: 'Goal batch partially saved',
      outcome: 'Partial save; remaining work blocked',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'Managing Goals or case links submits several writes. An earlier Goal change is confirmed, then a later change is rejected by a conflict, cap or business rule.',
      meaning:
        'The log records confirmed and planned write counts. Confirmed work is retained; no rollback is implied. An uncertain create remains unknown until authoritative reconciliation.',
      events: [
        {
          name: 'fa.action.blocked',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'goal_recovered',
      group: 'goals',
      label: 'Goal retry recovered',
      outcome: 'Recovered Goal save',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'After a blocked Goal save, the FA reviews refreshed state and retries the remaining changes. Creation, wording/status changes, and linking/unlinking cases use this shared workflow.',
      meaning:
        'Dialog retries retain the interaction ID even when some changes were already saved. A failed refresh after all writes is a feature refresh failure rather than a failed save.',
      events: [
        {
          name: 'fa.action.blocked',
          level: 'Warning',
        },
        {
          name: 'fa.action.recovered',
          level: 'Info',
        },
      ],
    },
    {
      id: 'review_retry_failed',
      group: 'fa',
      label: 'AI Review retry rejected',
      outcome: 'Blocked submission',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'The FA asks AI Review to retry an activity, but submission is rejected. The shared service covers both the queue row and detail dialog.',
      meaning:
        'Confirm and retry successes mean accepted for background execution; denial success means the denial was saved. They do not establish downstream execution success.',
      events: [
        {
          name: 'fa.action.blocked',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'send_unknown',
      group: 'fa',
      label: 'Send response lost',
      outcome: 'Send result unknown',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'Sending a Financial Snapshot or Credit Overview link loses the API response. The service may already have accepted the request.',
      meaning:
        'This records the send acceptance stage. It cannot confirm message delivery and should not be used as a delivery receipt.',
      events: [
        {
          name: 'fa.action.outcome_unknown',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'metadata_recovered',
      group: 'features',
      label: 'Client details refresh recovered',
      outcome: 'Refresh recovery',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'Loading client metadata fails, then a later refresh succeeds. A refresh-button request is identified as user-triggered; automatic loads and stale-cache refreshes are background work.',
      meaning:
        'Generated context is captured for the request and retained across retry. Client switching does not attach the old response to the new client. Client identifiers and header data are excluded.',
      events: [
        {
          name: 'feature.operation.failed',
          level: 'Warning',
        },
        {
          name: 'feature.operation.recovered',
          level: 'Info',
        },
      ],
    },
    {
      id: 'credit_render_failed',
      group: 'features',
      label: 'Credit report could not render',
      outcome: 'Rendering failure',
      detail: 'Generate a synthetic diagnostic without changing client data.',
      realCause:
        'Opening a credit report fails while loading the PDF library/document, reading a page, extracting text, or rendering its canvas. Navigation and destruction are excluded.',
      meaning:
        'The log records only the controlled rendering stage and reason. Existing safe exception reporting remains; report content is excluded.',
      events: [
        {
          name: 'feature.operation.failed',
          level: 'Warning',
        },
      ],
    },
    {
      id: 'token_refresh_failed',
      group: 'auth',
      label: 'Token refresh failed',
      detail:
        'Simulate a failed authentication refresh without changing your session.',
      outcome: 'Refresh failure',
      realCause:
        'Okta access-token renewal rejects or returns no usable access token. A connection problem or an expired or revoked session can cause renewal to fail.',
      meaning:
        'The app could not refresh authentication. The reason is refresh_failed or invalid_token; this event alone does not establish why Okta rejected the renewal.',
      events: [{ name: 'auth.token_refresh.failed', level: 'Warning' }],
    },
    {
      id: 'forced_logout',
      group: 'auth',
      label: 'Forced sign-out',
      detail: 'Generate a forced sign-out log. You stay signed in.',
      outcome: 'Sign-out requested',
      realCause:
        'An API response matches the app’s recognized authorization-denial condition and triggers forced sign-out. An ordinary manual sign-out does not generate this event.',
      meaning:
        'The app requested sign-out; the log is not confirmation that sign-out finished. Concurrent API handlers share one sign-out operation.',
      events: [{ name: 'auth.session.forced_logout', level: 'Warning' }],
    },
    {
      id: 'connector_timeout',
      group: 'connectors',
      label: 'Connector timed out',
      detail:
        'Simulate a connector connection timeout without opening a popup.',
      outcome: 'Connection failure',
      realCause:
        'After authorization starts, the OAuth popup stays open but the app receives no valid completion callback within five minutes. The external sign-in may be unfinished or its callback may not reach the app.',
      meaning:
        'The connection attempt timed out. Deliberately closing the popup is cancellation and does not produce a failure log.',
      events: [{ name: 'connector.connection.failed', level: 'Warning' }],
    },
    {
      id: 'connector_recovered',
      group: 'connectors',
      label: 'Connector recovered',
      detail: 'Simulate a blocked connector connection followed by recovery.',
      outcome: 'Failure then recovery',
      realCause:
        'A connector connection succeeds after a previous failure for the same connector within ten minutes in the current app session. For example, a blocked popup is allowed and the FA retries.',
      meaning:
        'The warning records the failed attempt; the info event records recovery. Both share an interaction ID, with an increased attempt count. Recovery is not another failure.',
      events: [
        { name: 'connector.connection.failed', level: 'Warning' },
        { name: 'connector.connection.recovered', level: 'Info' },
      ],
    },
    {
      id: 'permission_denied',
      group: 'fa',
      label: 'Permission denied',
      detail: 'Simulate an authorization rejection of an FA confirmation.',
      outcome: 'Action blocked',
      realCause:
        'An FA submits an AI Review confirmation and the API rejects it with HTTP 401 or 403. The user’s session or permissions may no longer authorize that operation.',
      meaning:
        'The submitted action was blocked. A disabled control for missing access is not a submitted attempt and does not generate this log. This test uses a 403 rejection.',
      events: [{ name: 'fa.action.blocked', level: 'Warning' }],
    },
    {
      id: 'response_lost',
      group: 'fa',
      label: 'Response lost',
      detail:
        'A case write loses its response. Whether it was saved is unknown.',
      outcome: 'Outcome unknown',
      realCause:
        'A case write was sent, but a network failure, timeout, or server error prevents the app from confirming whether it was saved.',
      meaning:
        'The server may already have saved the case. Check the authoritative case state before retrying; this is not proof that the write failed.',
      events: [{ name: 'fa.action.outcome_unknown', level: 'Warning' }],
    },
    {
      id: 'submission_failed',
      group: 'fa',
      label: 'Submission failed',
      detail: 'A technical failure prevents a case request from being sent.',
      outcome: 'Failure category test',
      realCause:
        'This category represents a local technical failure before a request is sent. Normal required-field validation and deliberate cancellation are not technical failures.',
      meaning:
        'A confirmed failure before dispatch differs from an uncertain write response. This button tests the shared classification and logging path.',
      coverageNote:
        'Synthetic coverage: the current case-create handler does not yet have a dedicated technical pre-submit failure path that emits this event.',
      events: [{ name: 'fa.action.failed', level: 'Error' }],
    },
    {
      id: 'retry_recovered',
      group: 'fa',
      label: 'Retry recovered',
      detail: 'A case conflict is followed by a successful retry.',
      outcome: 'Blocked then recovered',
      realCause:
        'A case write is rejected with a conflict, then a subsequent attempt in the same dialog is confirmed successful. This test simulates an HTTP 409 followed by success.',
      meaning:
        'The original attempt stays blocked; the new attempt is recovered. Separate attempt IDs share an interaction ID, and recovery_of_attempt_id points to the earlier attempt.',
      events: [
        { name: 'fa.action.blocked', level: 'Warning' },
        { name: 'fa.action.recovered', level: 'Info' },
      ],
    },
    {
      id: 'navi_recovered',
      group: 'navi',
      label: 'Navi reconnected',
      detail: 'An interrupted reply stream reconnects and recovers.',
      outcome: 'Interruption then recovery',
      realCause:
        'A Navi reply stream is interrupted and the client enters its reconnect loop. Temporary network loss or a service interruption can cause this. The run later reconciles without a reported run error.',
      meaning:
        'The warning describes the interruption; the info event describes recovery. A terminal failure is logged separately as copilot.run.failed. Conversation and run IDs are retained for investigation; message content and account details are excluded.',
      events: [
        { name: 'copilot.run.reconnecting', level: 'Warning' },
        { name: 'copilot.run.recovered', level: 'Info' },
      ],
    },
    {
      id: 'flags_failed',
      group: 'flags',
      label: 'Feature flags failed',
      detail: 'Feature-flag initialization fails.',
      outcome: 'Feature availability degraded',
      realCause:
        'LaunchDarkly initialization throws or fails its initialization wait. A blocked network request or unavailable flag service can cause this; some features may use their configured defaults.',
      meaning:
        'Feature availability may be degraded. Missing configuration and failed user identification have separate log events. This test does not change any flag values.',
      events: [
        { name: 'feature_flags.initialization_failed', level: 'Warning' },
      ],
    },
  ];

  readonly scenarioGroups = (
    [
      {
        id: 'api',
        title: 'API diagnostics',
        description: 'Request failures and unexpected response contracts.',
      },
      {
        id: 'actions',
        title: 'Care Plan \u00b7 Actions',
        description: 'Action creation, edits, status changes and reassignment.',
      },
      {
        id: 'outcomes',
        title: 'Care Plan \u00b7 Outcomes',
        description:
          'Case-linked and Unlinked Outcomes, including multi-step moves.',
      },
      {
        id: 'options',
        title: 'Care Plan \u00b7 Options',
        description:
          'Option saves, status, qualification requests and overrides.',
      },
      {
        id: 'goals',
        title: 'Care Plan \u00b7 Goals',
        description: 'Goal changes, case associations and partial batch saves.',
      },
      {
        id: 'features',
        title: 'Feature operations',
        description: 'Metadata refresh and credit-report rendering.',
      },
      {
        id: 'fa',
        title: 'FA actions',
        description:
          'Submitted work that is blocked, has an uncertain result, or succeeds after retry.',
      },
      {
        id: 'auth',
        title: 'Authentication',
        description:
          'Session renewal and sign-out requests. Test buttons leave your session unchanged.',
      },
      {
        id: 'connectors',
        title: 'Connectors',
        description:
          'Authorization connection failures and recovery. Test buttons open no popups.',
      },
      {
        id: 'navi',
        title: 'Navi',
        description: 'Reply-stream interruptions and recovery.',
      },
      {
        id: 'flags',
        title: 'Feature flags',
        description:
          'Problems loading the settings that control feature availability.',
      },
    ] satisfies Array<{ id: ScenarioGroup; title: string; description: string }>
  ).map((group) => ({
    ...group,
    scenarios: this.scenarios.filter((scenario) => scenario.group === group.id),
  }));

  private errorProbe(scenario: SentryErrorProbe): Error {
    const runId = this.testRunId();
    return createSentryErrorProbe(scenario, runId, () => {
      if (this.testRunId() === runId)
        this.errorProbeStatus.set(
          `SDK captured ${scenario.replaceAll('_', ' ')}. Confirm receipt in Sentry Issues using this run ID.`,
        );
    });
  }

  testAngularError(): never {
    this.errorProbeStatus.set(
      'Angular test error triggered. Confirm receipt in Sentry Issues using the run ID.',
    );
    // Angular must catch this through the actual template listener.
    throw this.errorProbe('angular_error');
  }

  testBrowserError(scenario: Exclude<SentryErrorProbe, 'angular_error'>): void {
    const error = this.errorProbe(scenario);
    this.errorProbeStatus.set(
      'Browser test error scheduled. Confirm receipt in Sentry Issues using the run ID.',
    );
    // Keep browser probes outside Angular so they exercise global SDK handlers.
    this.zone.runOutsideAngular(() => {
      if (scenario === 'browser_error')
        setTimeout(() => {
          throw error;
        }, 0);
      else void Promise.reject(error);
    });
  }

  newRun(): void {
    if (this.busy() || this.traceProbeBusy() || this.replayProbeBusy()) return;
    this.testRunId.set(logCorrelationId());
    this.results.set([]);
    this.errorProbeStatus.set(null);
    this.traceProbeStatus.set(null);
    this.replayProbeStatus.set(null);
    this.activeScenario.set(null);
  }

  async testTrace(): Promise<void> {
    if (this.traceProbeBusy()) return;
    this.traceProbeBusy.set(true);
    this.traceProbeStatus.set('Sending a synthetic trace to Sentry.');
    try {
      const result = await runSentryTraceProbe(this.testRunId());
      const response = result.statusCode;
      const delivery =
        response !== undefined
          ? response >= 200 && response < 300
            ? `Sentry transport accepted the trace (HTTP ${response}).`
            : `Sentry transport returned HTTP ${response}.`
          : result.flushCompleted
            ? 'SDK flush completed without a transport acknowledgement.'
            : 'SDK flush did not complete.';
      this.traceProbeStatus.set(
        `${delivery} Trace ID: ${result.traceId || 'not created'}. Confirm the trace and its fields in Sentry.`,
      );
    } catch {
      this.traceProbeStatus.set(
        'Trace verification could not complete. Check the connection and retry.',
      );
    } finally {
      this.traceProbeBusy.set(false);
    }
  }

  async testReplay(): Promise<void> {
    if (this.replayProbeBusy()) return;
    this.replayProbeBusy.set(true);
    this.replayProbeStatus.set(
      'Recording a short synthetic replay (about six seconds).',
    );
    try {
      const result = await runSentryReplayProbe(
        this.testRunId(),
        async () => {
          this.replayProbeVisible.set(true);
          await new Promise<void>((resolve) => setTimeout(resolve, 5500));
        },
        () => this.replayProbeVisible.set(false),
      );
      const delivery =
        result.acceptedSegments > 0 &&
        result.rejectedSegments === 0 &&
        result.blockedSegments === 0
          ? `Sentry transport accepted ${result.acceptedSegments} replay segment(s) (HTTP ${result.statusCode}).`
          : result.blockedSegments > 0
            ? 'Recording policy blocked the replay locally; no delivery is confirmed.'
            : 'Replay has no confirmed transport acceptance.';
      this.replayProbeStatus.set(
        `${delivery} Replay ID: ${result.replayId}. Open the recording in Sentry to verify masking and retained internal IDs.`,
      );
    } catch {
      this.replayProbeStatus.set(
        'Replay verification could not complete. No delivery is confirmed; check the connection or recording-policy failure.',
      );
    } finally {
      this.replayProbeVisible.set(false);
      this.replayProbeBusy.set(false);
    }
  }

  async run(scenario: LoggingScenario): Promise<void> {
    if (this.busy() || !this.enabled) return;
    this.activeScenario.set(scenario.id);
    this.busy.set(true);
    try {
      const result = await this.testing.run(scenario.id, this.testRunId());
      this.results.update((results) =>
        [{ ...result, label: scenario.label }, ...results].slice(0, 12),
      );
    } finally {
      this.busy.set(false);
    }
  }
}
